import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import FileUploadInput, { FILE_ACCEPT_TYPES } from '../../components/admin/FileUploadInput';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, Plus, Edit, Trash2, Save, X, Image as ImageIcon } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ABICManagement = () => {
  const [institute, setInstitute] = useState(null);
  const [stages, setStages] = useState([]);
  const [technologies, setTechnologies] = useState([]);
  const [startups, setStartups] = useState([]);
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Basic Info Form
  const [basicForm, setBasicForm] = useState({
    hero_banners: [''],
    about_description: '',
    statistics: [{ label: '', value: '', icon: 'Rocket' }],
    mandates: [''],
    key_activities: [''],
    contact_address: '',
    contact_phone: '',
    contact_email: '',
    linkages_logos: [{ name: '', link: '', icon: 'Award' }]
  });

  const [stageForm, setStageForm] = useState({ stage_title: '', stage_description: '', stage_icon: 'Lightbulb', priority: 1 });
  const [techForm, setTechForm] = useState({ title: '', description: '', link_url: '' });
  const [startupForm, setStartupForm] = useState({ serial_number: 1, startup_name: '', website_url: '', technology: '', year: '' });
  const [pubForm, setPubForm] = useState({ title: '', description: '', file_url: '', external_link: '' });

  const [editingId, setEditingId] = useState(null);
  const [openDialog, setOpenDialog] = useState(null);
  const [fileData, setFileData] = useState({});

  const iconOptions = ['Rocket', 'Users', 'Building2', 'Award', 'TrendingUp', 'Briefcase', 'Target', 'Lightbulb', 'FileText', 'Settings', 'Search', 'TestTube', 'Shield', 'Calculator', 'DollarSign', 'Link', 'Wrench', 'Leaf'];

  useEffect(() => {
    fetchData();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('cish_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchData = async () => {
    try {
      setLoading(true);

      const [instituteRes, stagesRes, techsRes, startupsRes, pubsRes] = await Promise.all([
        axios.get(`${API_URL}/api/institutes/abic`),
        axios.get(`${API_URL}/api/abic/incubation-stages`),
        axios.get(`${API_URL}/api/abic/technologies-commercialized`),
        axios.get(`${API_URL}/api/abic/startups`),
        axios.get(`${API_URL}/api/abic/publications`)
      ]);

      setInstitute(instituteRes.data);
      setBasicForm({
        hero_banners: instituteRes.data.hero_banners?.length > 0 ? instituteRes.data.hero_banners : [''],
        about_description: instituteRes.data.about_description || '',
        statistics: instituteRes.data.statistics?.length > 0 ? instituteRes.data.statistics : [{ label: '', value: '', icon: 'Rocket' }],
        mandates: instituteRes.data.mandates?.length > 0 ? instituteRes.data.mandates : [''],
        key_activities: instituteRes.data.key_activities?.length > 0 ? instituteRes.data.key_activities : [''],
        contact_address: instituteRes.data.contact_address || '',
        contact_phone: instituteRes.data.contact_phone || '',
        contact_email: instituteRes.data.contact_email || '',
        linkages_logos: instituteRes.data.linkages_logos?.length > 0 ? instituteRes.data.linkages_logos : [{ name: '', link: '', icon: 'Award' }]
      });

      setStages(stagesRes.data);
      setTechnologies(techsRes.data);
      setStartups(startupsRes.data);
      setPublications(pubsRes.data);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
      setLoading(false);
    }
  };

  const uploadFiles = async () => {
    const uploadedUrls = {};
    const uploadPromises = [];

    // Identify files to upload
    for (const [key, files] of Object.entries(fileData)) {
      const fileList = Array.isArray(files) ? files : [files];

      for (const file of fileList) {
        if (file instanceof File) {
          const formData = new FormData();
          formData.append('file', file);

          const promise = axios.post(`${API_URL}/api/files/upload?subfolder=abic`, formData, {
            headers: {
              ...getAuthHeaders(),
              'Content-Type': 'multipart/form-data'
            }
          }).then(response => {
            const url = `/api/files/${response.data.id}`;
            if (!uploadedUrls[key]) uploadedUrls[key] = [];
            uploadedUrls[key].push(url);
          });

          uploadPromises.push(promise);
        }
      }
    }

    if (uploadPromises.length > 0) {
      toast.info(`Uploading ${uploadPromises.length} files...`);
      await Promise.all(uploadPromises);
    }

    return uploadedUrls;
  };

  const saveBasicInfo = async () => {
    try {
      setSaving(true);

      // Upload files first
      const uploadedUrls = await uploadFiles();

      // Update form with new URLs
      let currentBanners = Array.isArray(basicForm.hero_banners) ? [...basicForm.hero_banners] : [];
      if (uploadedUrls.hero_banners && uploadedUrls.hero_banners.length > 0) {
        // Append new images to existing banners
        currentBanners = [...currentBanners, ...uploadedUrls.hero_banners];
      }

      // Filter out empty strings and ensure it's still an array
      const cleanedBanners = currentBanners.filter(b => typeof b === 'string' && b.trim() !== '');

      await axios.post(
        `${API_URL}/api/institutes`,
        {
          type: 'abic',
          name: 'Agri-Business Incubation Centre',
          short_name: 'ABIC',
          ...basicForm,
          hero_banners: cleanedBanners
        },
        { headers: getAuthHeaders() }
      );
      toast.success('Basic information saved successfully');
      setFileData({}); // Clear file data
      fetchData();
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save basic information');
    } finally {
      setSaving(false);
    }
  };

  // Stage CRUD
  const saveStage = async () => {
    try {
      if (editingId) {
        await axios.put(`${API_URL}/api/abic/incubation-stages/${editingId}`, stageForm, { headers: getAuthHeaders() });
        toast.success('Stage updated');
      } else {
        await axios.post(`${API_URL}/api/abic/incubation-stages`, stageForm, { headers: getAuthHeaders() });
        toast.success('Stage created');
      }
      setOpenDialog(null);
      setEditingId(null);
      setStageForm({ stage_title: '', stage_description: '', stage_icon: 'Lightbulb', priority: 1 });
      fetchData();
    } catch (error) {
      toast.error('Failed to save stage');
    }
  };

  const deleteStage = async (id) => {
    if (!window.confirm('Delete this stage?')) return;
    try {
      await axios.delete(`${API_URL}/api/abic/incubation-stages/${id}`, { headers: getAuthHeaders() });
      toast.success('Stage deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Technology CRUD
  const saveTechnology = async () => {
    try {
      if (editingId) {
        await axios.put(`${API_URL}/api/abic/technologies-commercialized/${editingId}`, techForm, { headers: getAuthHeaders() });
        toast.success('Technology updated');
      } else {
        await axios.post(`${API_URL}/api/abic/technologies-commercialized`, techForm, { headers: getAuthHeaders() });
        toast.success('Technology created');
      }
      setOpenDialog(null);
      setEditingId(null);
      setTechForm({ title: '', description: '', link_url: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to save technology');
    }
  };

  const deleteTechnology = async (id) => {
    if (!window.confirm('Delete this technology?')) return;
    try {
      await axios.delete(`${API_URL}/api/abic/technologies-commercialized/${id}`, { headers: getAuthHeaders() });
      toast.success('Technology deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Startup CRUD
  const saveStartup = async () => {
    try {
      if (editingId) {
        await axios.put(`${API_URL}/api/abic/startups/${editingId}`, startupForm, { headers: getAuthHeaders() });
        toast.success('Startup updated');
      } else {
        await axios.post(`${API_URL}/api/abic/startups`, startupForm, { headers: getAuthHeaders() });
        toast.success('Startup created');
      }
      setOpenDialog(null);
      setEditingId(null);
      setStartupForm({ serial_number: 1, startup_name: '', website_url: '', technology: '', year: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to save startup');
    }
  };

  const deleteStartup = async (id) => {
    if (!window.confirm('Delete this startup?')) return;
    try {
      await axios.delete(`${API_URL}/api/abic/startups/${id}`, { headers: getAuthHeaders() });
      toast.success('Startup deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Publication CRUD
  const savePublication = async () => {
    try {
      if (editingId) {
        await axios.put(`${API_URL}/api/abic/publications/${editingId}`, pubForm, { headers: getAuthHeaders() });
        toast.success('Publication updated');
      } else {
        await axios.post(`${API_URL}/api/abic/publications`, pubForm, { headers: getAuthHeaders() });
        toast.success('Publication created');
      }
      setOpenDialog(null);
      setEditingId(null);
      setPubForm({ title: '', description: '', file_url: '', external_link: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to save publication');
    }
  };

  const deletePublication = async (id) => {
    if (!window.confirm('Delete this publication?')) return;
    try {
      await axios.delete(`${API_URL}/api/abic/publications/${id}`, { headers: getAuthHeaders() });
      toast.success('Publication deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center min-h-screen">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-6">ABIC Management</h1>

        <Tabs defaultValue="basic" className="w-full">
          <TabsList className="grid w-full grid-cols-6 lg:grid-cols-11 mb-6">
            <TabsTrigger value="basic">Basic Info</TabsTrigger>
            <TabsTrigger value="banners">Banners</TabsTrigger>
            <TabsTrigger value="about">About</TabsTrigger>
            <TabsTrigger value="stats">Statistics</TabsTrigger>
            <TabsTrigger value="stages">Incubation Stages</TabsTrigger>
            <TabsTrigger value="technologies">Technologies</TabsTrigger>
            <TabsTrigger value="startups">Startups</TabsTrigger>
            <TabsTrigger value="publications">Publications</TabsTrigger>
            <TabsTrigger value="who-apply">Who Can Apply</TabsTrigger>
            <TabsTrigger value="how-apply">How to Apply</TabsTrigger>
            <TabsTrigger value="contact">Contact</TabsTrigger>
            <TabsTrigger value="summary">Summary Cards</TabsTrigger>
          </TabsList>

          {/* A) Banner Carousel Tab */}
          <TabsContent value="banners" className="space-y-4">
            <div className="bg-white p-6 rounded-lg border">
              <h2 className="text-lg font-semibold mb-4">Hero Banner Carousel</h2>
              <FileUploadInput
                field={{ name: 'hero_banners', type: 'file', multiple: true, fileType: 'image' }}
                value={fileData.hero_banners}
                existingUrl={basicForm.hero_banners}
                formData={basicForm}
                setFormData={setBasicForm}
                onChange={(files) => setFileData({ ...fileData, hero_banners: files })}
                onDeleteServerFile={async (path) => {
                  try {
                    const id = path.split('/').pop();
                    if (id) {
                      await axios.delete(`${API_URL}/api/files/${id}`, { headers: getAuthHeaders() });
                      toast.success('File deleted from server');
                      return true;
                    }
                  } catch (err) {
                    console.error('Delete error:', err);
                    toast.warning('File removed from list (Server lookup skipped)'); return true;
                  }
                  return true;
                }}
              />
              <Button onClick={saveBasicInfo} disabled={saving} className="mt-4">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                <span className="ml-2">Save Banners</span>
              </Button>
            </div>
          </TabsContent>

          {/* B) About ABIC Tab */}
          <TabsContent value="about" className="space-y-4">
            <div className="bg-white p-6 rounded-lg border">
              <h2 className="text-lg font-semibold mb-4">About ABIC (Rich Text)</h2>
              <Textarea
                rows={10}
                placeholder="Enter HTML content for About section"
                value={basicForm.about_description}
                onChange={(e) => setBasicForm({ ...basicForm, about_description: e.target.value })}
                className="font-mono text-sm"
              />
              <p className="text-sm text-gray-500 mt-2">You can use HTML tags: &lt;p&gt;, &lt;strong&gt;, &lt;em&gt;, &lt;ul&gt;, &lt;li&gt;</p>
              <Button onClick={saveBasicInfo} disabled={saving} className="mt-4">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                <span className="ml-2">Save About</span>
              </Button>
            </div>
          </TabsContent>

          {/* C) Statistics Tab */}
          <TabsContent value="stats" className="space-y-4">
            <div className="bg-white p-6 rounded-lg border">
              <h2 className="text-lg font-semibold mb-4">ABIC Statistics (4-6 items)</h2>
              {basicForm.statistics.map((stat, index) => (
                <div key={index} className="grid grid-cols-4 gap-3 mb-3 items-end">
                  <div>
                    <Label>Metric Title</Label>
                    <Input
                      placeholder="e.g., Startups Incubated"
                      value={stat.label}
                      onChange={(e) => {
                        const updated = [...basicForm.statistics];
                        updated[index].label = e.target.value;
                        setBasicForm({ ...basicForm, statistics: updated });
                      }}
                    />
                  </div>
                  <div>
                    <Label>Value</Label>
                    <Input
                      placeholder="e.g., 15+"
                      value={stat.value}
                      onChange={(e) => {
                        const updated = [...basicForm.statistics];
                        updated[index].value = e.target.value;
                        setBasicForm({ ...basicForm, statistics: updated });
                      }}
                    />
                  </div>
                  <div>
                    <Label>Icon</Label>
                    <Select
                      value={stat.icon}
                      onValueChange={(value) => {
                        const updated = [...basicForm.statistics];
                        updated[index].icon = value;
                        setBasicForm({ ...basicForm, statistics: updated });
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {iconOptions.map(icon => <SelectItem key={icon} value={icon}>{icon}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      const updated = basicForm.statistics.filter((_, i) => i !== index);
                      setBasicForm({ ...basicForm, statistics: updated.length ? updated : [{ label: '', value: '', icon: 'Rocket' }] });
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button
                onClick={() => setBasicForm({ ...basicForm, statistics: [...basicForm.statistics, { label: '', value: '', icon: 'Rocket' }] })}
                variant="outline"
                size="sm"
                className="mt-2"
              >
                <Plus className="h-4 w-4 mr-2" /> Add Statistic
              </Button>
              <Button onClick={saveBasicInfo} disabled={saving} className="ml-3">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                <span className="ml-2">Save Statistics</span>
              </Button>
            </div>
          </TabsContent>

          {/* D) Incubation Stages Tab */}
          <TabsContent value="stages" className="space-y-4">
            <div className="bg-white p-6 rounded-lg border">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Incubation Stages ({stages.length})</h2>
                <Dialog open={openDialog === 'stage'} onOpenChange={(open) => !open && setOpenDialog(null)}>
                  <DialogTrigger asChild>
                    <Button onClick={() => { setEditingId(null); setStageForm({ stage_title: '', stage_description: '', stage_icon: 'Lightbulb', priority: stages.length + 1 }); setOpenDialog('stage'); }}>
                      <Plus className="h-4 w-4 mr-2" /> Add Stage
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{editingId ? 'Edit' : 'Add'} Incubation Stage</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label>Stage Title</Label>
                        <Input value={stageForm.stage_title} onChange={(e) => setStageForm({ ...stageForm, stage_title: e.target.value })} />
                      </div>
                      <div>
                        <Label>Description (Optional)</Label>
                        <Textarea value={stageForm.stage_description} onChange={(e) => setStageForm({ ...stageForm, stage_description: e.target.value })} />
                      </div>
                      <div>
                        <Label>Icon</Label>
                        <Select value={stageForm.stage_icon} onValueChange={(value) => setStageForm({ ...stageForm, stage_icon: value })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {iconOptions.map(icon => <SelectItem key={icon} value={icon}>{icon}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Priority Order</Label>
                        <Input type="number" value={stageForm.priority} onChange={(e) => setStageForm({ ...stageForm, priority: parseInt(e.target.value) })} />
                      </div>
                      <Button onClick={saveStage} className="w-full">Save Stage</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
              <div className="space-y-2">
                {stages.sort((a, b) => a.priority - b.priority).map((stage) => (
                  <div key={stage.id} className="flex items-center justify-between p-3 border rounded">
                    <div>
                      <p className="font-medium">{stage.priority}. {stage.stage_title}</p>
                      <p className="text-sm text-gray-500">{stage.stage_description}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => { setEditingId(stage.id); setStageForm(stage); setOpenDialog('stage'); }}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => deleteStage(stage.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* E) Technologies Tab */}
          <TabsContent value="technologies" className="space-y-4">
            <div className="bg-white p-6 rounded-lg border">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Technologies Commercialized ({technologies.length})</h2>
                <Dialog open={openDialog === 'tech'} onOpenChange={(open) => !open && setOpenDialog(null)}>
                  <DialogTrigger asChild>
                    <Button onClick={() => { setEditingId(null); setTechForm({ title: '', description: '', link_url: '' }); setOpenDialog('tech'); }}>
                      <Plus className="h-4 w-4 mr-2" /> Add Technology
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{editingId ? 'Edit' : 'Add'} Technology</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label>Title</Label>
                        <Input value={techForm.title} onChange={(e) => setTechForm({ ...techForm, title: e.target.value })} />
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Textarea rows={4} value={techForm.description} onChange={(e) => setTechForm({ ...techForm, description: e.target.value })} />
                      </div>
                      <div>
                        <Label>Link URL (Optional)</Label>
                        <Input value={techForm.link_url} onChange={(e) => setTechForm({ ...techForm, link_url: e.target.value })} />
                      </div>
                      <Button onClick={saveTechnology} className="w-full">Save Technology</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
              <div className="space-y-2">
                {technologies.map((tech) => (
                  <div key={tech.id} className="flex items-start justify-between p-3 border rounded">
                    <div className="flex-1">
                      <p className="font-medium">{tech.title}</p>
                      <p className="text-sm text-gray-600 mt-1">{tech.description}</p>
                      {tech.link_url && <p className="text-xs text-blue-500 mt-1">{tech.link_url}</p>}
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => { setEditingId(tech.id); setTechForm(tech); setOpenDialog('tech'); }}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => deleteTechnology(tech.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* F) Incubated Startups Tab */}
          <TabsContent value="startups" className="space-y-4">
            <div className="bg-white p-6 rounded-lg border">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Incubated Startups ({startups.length})</h2>
                <Dialog open={openDialog === 'startup'} onOpenChange={(open) => !open && setOpenDialog(null)}>
                  <DialogTrigger asChild>
                    <Button onClick={() => { setEditingId(null); setStartupForm({ serial_number: startups.length + 1, startup_name: '', website_url: '', technology: '', year: '' }); setOpenDialog('startup'); }}>
                      <Plus className="h-4 w-4 mr-2" /> Add Startup
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{editingId ? 'Edit' : 'Add'} Startup</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label>Serial Number</Label>
                        <Input type="number" value={startupForm.serial_number} onChange={(e) => setStartupForm({ ...startupForm, serial_number: parseInt(e.target.value) })} />
                      </div>
                      <div>
                        <Label>Startup Name</Label>
                        <Input value={startupForm.startup_name} onChange={(e) => setStartupForm({ ...startupForm, startup_name: e.target.value })} />
                      </div>
                      <div>
                        <Label>Website URL (Optional)</Label>
                        <Input value={startupForm.website_url} onChange={(e) => setStartupForm({ ...startupForm, website_url: e.target.value })} />
                      </div>
                      <div>
                        <Label>Technology / Domain</Label>
                        <Input value={startupForm.technology} onChange={(e) => setStartupForm({ ...startupForm, technology: e.target.value })} />
                      </div>
                      <div>
                        <Label>Year (Optional)</Label>
                        <Input value={startupForm.year} onChange={(e) => setStartupForm({ ...startupForm, year: e.target.value })} />
                      </div>
                      <Button onClick={saveStartup} className="w-full">Save Startup</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
              <div className="space-y-2">
                {startups.sort((a, b) => a.serial_number - b.serial_number).map((startup) => (
                  <div key={startup.id} className="flex items-center justify-between p-3 border rounded">
                    <div className="flex-1 grid grid-cols-4 gap-4">
                      <p className="font-medium">{startup.serial_number}</p>
                      <p className="font-medium">{startup.startup_name}</p>
                      <p className="text-sm text-gray-600">{startup.technology}</p>
                      <p className="text-sm text-gray-500">{startup.year || '—'}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => { setEditingId(startup.id); setStartupForm(startup); setOpenDialog('startup'); }}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => deleteStartup(startup.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* G) Publications Tab */}
          <TabsContent value="publications" className="space-y-4">
            <div className="bg-white p-6 rounded-lg border">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Publications & Reports ({publications.length})</h2>
                <Dialog open={openDialog === 'pub'} onOpenChange={(open) => !open && setOpenDialog(null)}>
                  <DialogTrigger asChild>
                    <Button onClick={() => { setEditingId(null); setPubForm({ title: '', description: '', file_url: '', external_link: '' }); setOpenDialog('pub'); }}>
                      <Plus className="h-4 w-4 mr-2" /> Add Publication
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{editingId ? 'Edit' : 'Add'} Publication</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label>Title</Label>
                        <Input value={pubForm.title} onChange={(e) => setPubForm({ ...pubForm, title: e.target.value })} />
                      </div>
                      <div>
                        <Label>Short Description</Label>
                        <Textarea rows={3} value={pubForm.description} onChange={(e) => setPubForm({ ...pubForm, description: e.target.value })} />
                      </div>
                      <div>
                        <Label>File URL</Label>
                        <Input value={pubForm.file_url} onChange={(e) => setPubForm({ ...pubForm, file_url: e.target.value })} />
                      </div>
                      <div>
                        <Label>External Link (alternative)</Label>
                        <Input value={pubForm.external_link} onChange={(e) => setPubForm({ ...pubForm, external_link: e.target.value })} />
                      </div>
                      <Button onClick={savePublication} className="w-full">Save Publication</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
              <div className="space-y-2">
                {publications.map((pub) => (
                  <div key={pub.id} className="flex items-start justify-between p-3 border rounded">
                    <div className="flex-1">
                      <p className="font-medium">{pub.title}</p>
                      <p className="text-sm text-gray-600 mt-1">{pub.description}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => { setEditingId(pub.id); setPubForm(pub); setOpenDialog('pub'); }}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => deletePublication(pub.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* H) Who Can Apply Tab */}
          <TabsContent value="who-apply" className="space-y-4">
            <div className="bg-white p-6 rounded-lg border">
              <h2 className="text-lg font-semibold mb-4">Who Can Apply (Bullet Points)</h2>
              {basicForm.mandates.map((item, index) => (
                <div key={index} className="flex gap-2 mb-3">
                  <Input
                    placeholder="Bullet point text"
                    value={item}
                    onChange={(e) => {
                      const updated = [...basicForm.mandates];
                      updated[index] = e.target.value;
                      setBasicForm({ ...basicForm, mandates: updated });
                    }}
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      const updated = basicForm.mandates.filter((_, i) => i !== index);
                      setBasicForm({ ...basicForm, mandates: updated.length ? updated : [''] });
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button
                onClick={() => setBasicForm({ ...basicForm, mandates: [...basicForm.mandates, ''] })}
                variant="outline"
                size="sm"
                className="mt-2"
              >
                <Plus className="h-4 w-4 mr-2" /> Add Bullet
              </Button>
              <Button onClick={saveBasicInfo} disabled={saving} className="ml-3">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                <span className="ml-2">Save</span>
              </Button>
            </div>
          </TabsContent>

          {/* I) How to Apply Tab */}
          <TabsContent value="how-apply" className="space-y-4">
            <div className="bg-white p-6 rounded-lg border">
              <h2 className="text-lg font-semibold mb-4">How to Apply (Bullet Points)</h2>
              {basicForm.key_activities.map((item, index) => (
                <div key={index} className="flex gap-2 mb-3">
                  <Input
                    placeholder="Bullet point text"
                    value={item}
                    onChange={(e) => {
                      const updated = [...basicForm.key_activities];
                      updated[index] = e.target.value;
                      setBasicForm({ ...basicForm, key_activities: updated });
                    }}
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      const updated = basicForm.key_activities.filter((_, i) => i !== index);
                      setBasicForm({ ...basicForm, key_activities: updated.length ? updated : [''] });
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button
                onClick={() => setBasicForm({ ...basicForm, key_activities: [...basicForm.key_activities, ''] })}
                variant="outline"
                size="sm"
                className="mt-2"
              >
                <Plus className="h-4 w-4 mr-2" /> Add Bullet
              </Button>
              <Button onClick={saveBasicInfo} disabled={saving} className="ml-3">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                <span className="ml-2">Save</span>
              </Button>
            </div>
          </TabsContent>

          {/* J) Contact Tab */}
          <TabsContent value="contact" className="space-y-4">
            <div className="bg-white p-6 rounded-lg border">
              <h2 className="text-lg font-semibold mb-4">Contact Information</h2>
              <div className="space-y-4">
                <div>
                  <Label>Address</Label>
                  <Textarea
                    value={basicForm.contact_address}
                    onChange={(e) => setBasicForm({ ...basicForm, contact_address: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={basicForm.contact_phone}
                    onChange={(e) => setBasicForm({ ...basicForm, contact_phone: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={basicForm.contact_email}
                    onChange={(e) => setBasicForm({ ...basicForm, contact_email: e.target.value })}
                  />
                </div>
                <Button onClick={saveBasicInfo} disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  <span className="ml-2">Save Contact</span>
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* K) Summary Cards Tab */}
          <TabsContent value="summary" className="space-y-4">
            <div className="bg-white p-6 rounded-lg border">
              <h2 className="text-lg font-semibold mb-4">Summary Cards (Bottom Section)</h2>
              {basicForm.linkages_logos.map((card, index) => (
                <div key={index} className="grid grid-cols-4 gap-3 mb-3 items-end">
                  <div>
                    <Label>Title/Value</Label>
                    <Input
                      placeholder="e.g., 15+"
                      value={card.name}
                      onChange={(e) => {
                        const updated = [...basicForm.linkages_logos];
                        updated[index].name = e.target.value;
                        setBasicForm({ ...basicForm, linkages_logos: updated });
                      }}
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Input
                      placeholder="e.g., Startups Incubated"
                      value={card.link}
                      onChange={(e) => {
                        const updated = [...basicForm.linkages_logos];
                        updated[index].link = e.target.value;
                        setBasicForm({ ...basicForm, linkages_logos: updated });
                      }}
                    />
                  </div>
                  <div>
                    <Label>Icon</Label>
                    <Select
                      value={card.icon}
                      onValueChange={(value) => {
                        const updated = [...basicForm.linkages_logos];
                        updated[index].icon = value;
                        setBasicForm({ ...basicForm, linkages_logos: updated });
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {iconOptions.map(icon => <SelectItem key={icon} value={icon}>{icon}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      const updated = basicForm.linkages_logos.filter((_, i) => i !== index);
                      setBasicForm({ ...basicForm, linkages_logos: updated.length ? updated : [{ name: '', link: '', icon: 'Award' }] });
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button
                onClick={() => setBasicForm({ ...basicForm, linkages_logos: [...basicForm.linkages_logos, { name: '', link: '', icon: 'Award' }] })}
                variant="outline"
                size="sm"
                className="mt-2"
              >
                <Plus className="h-4 w-4 mr-2" /> Add Card
              </Button>
              <Button onClick={saveBasicInfo} disabled={saving} className="ml-3">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                <span className="ml-2">Save Summary Cards</span>
              </Button>
            </div>
          </TabsContent>

          {/* Basic Info Tab (overview) */}
          <TabsContent value="basic" className="space-y-4">
            <div className="bg-white p-6 rounded-lg border">
              <h2 className="text-lg font-semibold mb-4">ABIC Basic Information Overview</h2>
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 bg-blue-50 rounded">
                    <p className="font-semibold text-blue-900">Banners</p>
                    <p className="text-2xl font-bold text-blue-600">{basicForm.hero_banners.filter(b => b).length}</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded">
                    <p className="font-semibold text-green-900">Statistics</p>
                    <p className="text-2xl font-bold text-green-600">{basicForm.statistics.length}</p>
                  </div>
                  <div className="p-4 bg-purple-50 rounded">
                    <p className="font-semibold text-purple-900">Incubation Stages</p>
                    <p className="text-2xl font-bold text-purple-600">{stages.length}</p>
                  </div>
                  <div className="p-4 bg-orange-50 rounded">
                    <p className="font-semibold text-orange-900">Technologies</p>
                    <p className="text-2xl font-bold text-orange-600">{technologies.length}</p>
                  </div>
                  <div className="p-4 bg-pink-50 rounded">
                    <p className="font-semibold text-pink-900">Startups</p>
                    <p className="text-2xl font-bold text-pink-600">{startups.length}</p>
                  </div>
                  <div className="p-4 bg-indigo-50 rounded">
                    <p className="font-semibold text-indigo-900">Publications</p>
                    <p className="text-2xl font-bold text-indigo-600">{publications.length}</p>
                  </div>
                </div>
                <div className="mt-6 p-4 bg-gray-50 rounded">
                  <p className="text-gray-700">Use the tabs above to manage different sections of the ABIC page. All changes are saved independently per section.</p>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default ABICManagement;
